import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { localStorageConstants } from '@/types/LocalStorageConstants';
import { getSubdomain } from '@/utils/getSubdomain';
import { Alert, Button, Container, Row } from '@cof/omni-react';
import { useBowserParser } from '@hooks/useBowserParser';

type AlertVariantType =
  | 'information'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

const { DEV } = import.meta.env;
const { testingHarness } = localStorageConstants;

export const TestingHarness = ({
  testOverride,
}: {
  testOverride?: boolean;
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (
      (!DEV && window.location.hostname !== 'd2iklivyxhpyxt.cloudfront.net') ||
      testOverride === true
    )
      navigate('/');
  }, [navigate, testOverride]);

  const bowser = useBowserParser();
  const bowserWithSubDomain = JSON.stringify(
    {
      ...bowser,
      subDomain: getSubdomain(), //append subDomain
    },
    null,
    2,
  );

  const [json, setJson] = useState<string>('');
  const [alertInfo, setAlertInfo] = useState<{
    variant: AlertVariantType;
    body: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJson(e.target.value);
  };

  const loadJson = () => {
    const localJson = localStorage.getItem(testingHarness);
    if (localJson) {
      setJson(localJson);
      setAlertInfo({
        variant: 'information',
        body: 'Loaded Test Harness JSON from localStorage',
      });
    } else {
      setJson(bowserWithSubDomain);
      setAlertInfo({ variant: 'information', body: 'Loaded Bower JSON' });
    }
  };

  const applyJson = () => {
    try {
      JSON.parse(json);
      localStorage.setItem(testingHarness, json);
      setAlertInfo({
        variant: 'success',
        body: 'Testing Values Successfully Applied',
      });
    } catch (e) {
      setAlertInfo({ variant: 'error', body: 'JSON Failed to Parse' });
    }
  };

  const removeJson = () => {
    localStorage.removeItem(testingHarness);
    setJson('');
    setAlertInfo({
      variant: 'information',
      body: "Harness has been reset, you are now using your browser's real useragent",
    });
  };

  const handleDismiss = () => {
    setAlertInfo(null);
  };

  return (
    <Container data-testid="TestingHarness">
      <Row justifyContent="center" paddingTop="large">
        <h1>Testing Harness Page</h1>
        <p style={{ textAlign: 'center' }}>
          set the JSON below to the device/browser you'd like to impersonate
        </p>
        <textarea
          data-testid="TestingHarness--TextArea"
          rows={30}
          style={{ width: '80%', marginTop: '20px' }}
          value={json}
          onChange={handleChange}
        />
      </Row>
      <Row justifyContent="center">
        {alertInfo && (
          <Alert
            data-testid="TestingHarness--Alert"
            variant={alertInfo.variant}
            dismissable
            onDismiss={handleDismiss}
          >
            {alertInfo.body}
          </Alert>
        )}
      </Row>
      <Row justifyContent="center" gap="small" style={{ marginTop: '10px' }}>
        <Button
          data-testid="TestingHarness--ApplyButton"
          rank="primary"
          onClick={applyJson}
        >
          Apply
        </Button>
        <Button data-testid="TestingHarness--LoadButton" onClick={loadJson}>
          Load
        </Button>
        <Button
          data-testid="TestingHarness--ResetButton"
          variant="critical"
          onClick={removeJson}
        >
          Reset
        </Button>
      </Row>
    </Container>
  );
};
